/**
 * Investigation graph panel — full-screen D3 force-directed entity graph.
 * Replaces the dashboard when an investigation graph is opened.
 *
 * Work packages: WP-S9.1 (graph component), WP-S9.2 (interaction),
 * WP-S9.3 (node detail sidebar), WP-S9.4 (collection log + coverage + status bar),
 * WP-S9.5 (graph view layout).
 *
 * Reference: ARCHITECTURE.md sections 3, 5, 7; UI-REFERENCE.html graph view.
 */

import * as d3 from 'd3';
import { eventBus } from '@/core/events';
import { escapeHtml } from '@/utils/sanitize';
import type {
  Entity,
  EntityType,
  Relationship,
} from '@/intelligence/entities/types';
import type { CorroborationMatch } from '@/intelligence/investigation/corroboration';
import { addLogEntry, updateLastActivity } from '@/intelligence/investigation/investigation-store';
import type {
  Investigation,
  InvestigationState,
  ResourceState,
  CoverageReport,
  DraftAssessment,
  LogEntry,
} from '@/intelligence/investigation/types';

// ---------------------------------------------------------------------------
// Entity type colors — matching UI-REFERENCE.html
// ---------------------------------------------------------------------------

const TYPE_COLORS: Record<EntityType, string> = {
  ASSET: '#00d4ff',
  ORGANIZATION: '#ff4444',
  LOCATION: '#44ff44',
  PERSON: '#ff8844',
  EVENT: '#ff44aa',
  INDICATOR: '#aa44ff',
  SOURCE: '#44ffcc',
};

const TYPE_LABELS: Record<EntityType, string> = {
  ASSET: 'Asset',
  ORGANIZATION: 'Organization',
  LOCATION: 'Location',
  PERSON: 'Person',
  EVENT: 'Event',
  INDICATOR: 'Indicator',
  SOURCE: 'Source',
};

// ---------------------------------------------------------------------------
// Graph data types (internal representation for D3)
// ---------------------------------------------------------------------------

interface GraphNode extends d3.SimulationNodeDatum {
  id: string;
  label: string;
  type: EntityType;
  entity: Entity;
  relationshipCount: number;
}

interface GraphLink extends d3.SimulationLinkDatum<GraphNode> {
  id: string;
  label: string;
  confidence: number;
  relationship: Relationship;
}

interface GraphData {
  nodes: GraphNode[];
  links: GraphLink[];
}

// ---------------------------------------------------------------------------
// NATO rating labels
// ---------------------------------------------------------------------------

const RELIABILITY_LABELS: Record<string, string> = {
  A: 'Completely reliable',
  B: 'Usually reliable',
  C: 'Fairly reliable',
  D: 'Not usually reliable',
  E: 'Unreliable',
  F: 'Cannot be judged',
};

// ---------------------------------------------------------------------------
// GraphPanel class — full-screen graph visualization
// ---------------------------------------------------------------------------

export class GraphPanel {
  private container: HTMLElement;
  private svgContainer: HTMLElement | null = null;
  private sidebarContainer: HTMLElement | null = null;
  private statusBar: HTMLElement | null = null;
  private headerEl: HTMLElement | null = null;
  private cleanupFns: (() => void)[] = [];
  private isOpen = false;

  // D3 state
  private simulation: d3.Simulation<GraphNode, GraphLink> | null = null;
  private svgElement: SVGSVGElement | null = null;
  // Data state
  private investigation: Investigation | null = null;
  private graphData: GraphData = { nodes: [], links: [] };
  private selectedNode: GraphNode | null = null;
  private resourceState: ResourceState | null = null;
  private coverageReport: CoverageReport | null = null;
  private logEntries: LogEntry[] = [];
  private completedPhases: Set<InvestigationState> = new Set();

  // Confidence filter — WP-S11.4
  private minCredibility: number = 6; // 1-6, 6 = show all (no filter)

  // Synthesis / Assessment — WP-S15.5
  private draftAssessment: DraftAssessment | null = null;
  private assessmentGenerating = false;

  // Weak matches requiring analyst review (from corroboration)
  private weakMatches: CorroborationMatch[] = [];
  private mergeDialogEl: HTMLElement | null = null;

  // Callbacks
  private onClose: (() => void) | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.className = 'graph-panel-root';
    this.container.style.cssText = `
      position: fixed; inset: 0; z-index: 10000;
      display: flex; flex-direction: column;
      background: #080b12; color: #e2e8f0;
      font-family: 'JetBrains Mono', 'Cascadia Code', monospace;
      overflow: hidden;
    `;

    this.buildLayout();
    this.injectKeyframes();
    this.wireEvents();
  }

  get element(): HTMLElement {
    return this.container;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /** Open the graph view for an investigation with its entities and relationships. */
  open(
    investigation: Investigation,
    entities: Entity[],
    relationships: Relationship[],
    onClose: () => void,
    weakMatches?: CorroborationMatch[],
  ): void {
    this.investigation = investigation;
    this.onClose = onClose;
    this.selectedNode = null;
    this.completedPhases.clear();
    this.weakMatches = weakMatches ?? [];
    this.logEntries = [];
    this.resourceState = null;
    this.coverageReport = null;
    this.minCredibility = 6; // Reset filter — show all
    this.draftAssessment = null;
    this.assessmentGenerating = false;
    this.graphData = this.buildGraphData(entities, relationships);

    this.updateHeader();
    this.renderGraph();
    this.renderSidebar();
    this.updateStatusBar();

    // Fade-in transition for smooth dashboard → graph switch
    this.container.style.opacity = '0';
    this.container.style.transition = 'opacity 200ms ease';
    document.body.appendChild(this.container);
    // Force layout reflow then animate
    void this.container.offsetHeight;
    this.container.style.opacity = '1';
    this.isOpen = true;
  }

  /** Close the graph view. */
  close(): void {
    this.isOpen = false;
    this.stopSimulation();
    // Fade-out transition for smooth graph → dashboard switch
    this.container.style.opacity = '0';
    setTimeout(() => {
      this.container.remove();
      if (this.onClose) this.onClose();
    }, 200);
  }

  /** Add an entity incrementally (progressive disclosure). */
  addEntity(entity: Entity): void {
    const existing = this.graphData.nodes.find(n => n.id === entity.id);
    if (existing) return;

    const node: GraphNode = {
      id: entity.id,
      label: entity.name,
      type: entity.type,
      entity,
      relationshipCount: 0,
    };
    this.graphData.nodes.push(node);
    this.refreshGraph();
  }

  /** Add a relationship incrementally. */
  addRelationship(rel: Relationship): void {
    const existingLink = this.graphData.links.find(l => l.id === rel.id);
    if (existingLink) return;

    const sourceNode = this.graphData.nodes.find(n => n.id === rel.sourceEntityId);
    const targetNode = this.graphData.nodes.find(n => n.id === rel.targetEntityId);
    if (!sourceNode || !targetNode) return;

    sourceNode.relationshipCount++;
    targetNode.relationshipCount++;

    const link: GraphLink = {
      id: rel.id,
      source: sourceNode,
      target: targetNode,
      label: rel.type,
      confidence: rel.confidence,
      relationship: rel,
    };
    this.graphData.links.push(link);
    this.refreshGraph();
  }

  /** Update resource state display. */
  setResourceState(state: ResourceState): void {
    this.resourceState = state;
    this.updateStatusBar();
  }

  /** Update coverage report display. */
  setCoverageReport(report: CoverageReport): void {
    this.coverageReport = report;
    this.renderCoverageSection();
  }

  /** Record a completed investigation phase. */
  addCompletedPhase(state: InvestigationState): void {
    this.completedPhases.add(state);
    this.renderCollectionLog();
  }

  /** Add a log entry. */
  addLogEntry(entry: LogEntry): void {
    this.logEntries.push(entry);
  }

  /** Set the draft assessment (from synthesis). */
  setDraftAssessment(assessment: DraftAssessment): void {
    this.draftAssessment = assessment;
    this.assessmentGenerating = false;
    this.renderAssessmentSection();
    this.updateHeader();
  }

  /** Destroy and clean up. */
  destroy(): void {
    this.stopSimulation();
    for (const fn of this.cleanupFns) fn();
    this.cleanupFns.length = 0;
    this.container.remove();
  }

  // ---------------------------------------------------------------------------
  // Layout — WP-S9.5
  // ---------------------------------------------------------------------------

  private buildLayout(): void {
    // Header
    this.headerEl = document.createElement('div');
    this.headerEl.style.cssText = `
      height: 42px; border-bottom: 1px solid #1e293b; background: #0a0e17;
      display: flex; align-items: center; justify-content: space-between;
      padding: 0 16px; flex-shrink: 0;
    `;
    this.container.appendChild(this.headerEl);

    // Main area: graph canvas + right sidebar
    const main = document.createElement('div');
    main.style.cssText = 'flex: 1; display: flex; overflow: hidden;';

    // Graph canvas
    this.svgContainer = document.createElement('div');
    this.svgContainer.style.cssText = `
      flex: 1; position: relative;
      background: radial-gradient(ellipse at center, #0d1220 0%, #080b12 70%);
    `;

    // Subtle grid overlay
    const gridOverlay = document.createElement('div');
    gridOverlay.style.cssText = `
      position: absolute; inset: 0; pointer-events: none;
      background-image: linear-gradient(rgba(30,41,59,0.15) 1px, transparent 1px),
                         linear-gradient(90deg, rgba(30,41,59,0.15) 1px, transparent 1px);
      background-size: 50px 50px;
    `;
    this.svgContainer.appendChild(gridOverlay);

    main.appendChild(this.svgContainer);

    // Right sidebar
    this.sidebarContainer = document.createElement('div');
    this.sidebarContainer.style.cssText = `
      width: 280px; border-left: 1px solid #1e293b; background: #0a0e17;
      display: flex; flex-direction: column; flex-shrink: 0; overflow: hidden;
    `;
    main.appendChild(this.sidebarContainer);

    this.container.appendChild(main);

    // Status bar
    this.statusBar = document.createElement('div');
    this.statusBar.style.cssText = `
      height: 24px; border-top: 1px solid #1e293b; background: #0a0e17;
      display: flex; align-items: center; justify-content: space-between;
      padding: 0 16px; flex-shrink: 0;
    `;
    this.container.appendChild(this.statusBar);
  }

  /** Inject CSS keyframes needed by the graph panel. */
  private injectKeyframes(): void {
    if (document.getElementById('graph-panel-keyframes')) return;
    const style = document.createElement('style');
    style.id = 'graph-panel-keyframes';
    style.textContent = `
      @keyframes graph-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.4; }
      }
    `;
    document.head.appendChild(style);
  }

  private updateHeader(): void {
    if (!this.headerEl || !this.investigation) return;
    this.headerEl.innerHTML = '';

    const left = document.createElement('div');
    left.style.cssText = 'display: flex; align-items: center; gap: 12px;';

    // Back button
    const backBtn = document.createElement('button');
    backBtn.style.cssText = `
      background: none; border: 1px solid #1e293b; color: #64748b;
      padding: 4px 10px; font-family: inherit; font-size: 10px;
      cursor: pointer; border-radius: 3px; letter-spacing: 1px;
    `;
    backBtn.textContent = '\u2190 DASHBOARD';
    backBtn.addEventListener('mouseenter', () => {
      backBtn.style.borderColor = '#00b4d8';
      backBtn.style.color = '#00b4d8';
    });
    backBtn.addEventListener('mouseleave', () => {
      backBtn.style.borderColor = '#1e293b';
      backBtn.style.color = '#64748b';
    });
    backBtn.addEventListener('click', () => this.close());
    left.appendChild(backBtn);

    // Separator
    const sep = document.createElement('span');
    sep.style.cssText = 'color: #334155;';
    sep.textContent = '\u2502';
    left.appendChild(sep);

    // "INVESTIGATION" label
    const label = document.createElement('span');
    label.style.cssText = 'font-size: 10px; font-weight: 600; letter-spacing: 2px; color: #64748b;';
    label.textContent = 'INVESTIGATION';
    left.appendChild(label);

    // Query chip
    const chip = document.createElement('span');
    chip.style.cssText = `
      font-size: 10px; color: #00b4d8; padding: 2px 8px;
      background: #00b4d811; border: 1px solid #00b4d833; border-radius: 2px;
    `;
    chip.textContent = `"${escapeHtml(this.investigation.rawQuery)}"`;
    left.appendChild(chip);

    this.headerEl.appendChild(left);

    // Right side: actions + metadata
    const right = document.createElement('div');
    right.style.cssText = 'display: flex; align-items: center; gap: 16px;';

    // "Generate Assessment" button — only on review state when no assessment yet
    if (this.investigation.state === 'review' && !this.draftAssessment && !this.assessmentGenerating) {
      const synthBtn = document.createElement('button');
      synthBtn.style.cssText = `
        background: #00b4d8; border: none; color: #080b12;
        padding: 4px 12px; font-family: inherit; font-size: 9px;
        cursor: pointer; border-radius: 3px; font-weight: 700; letter-spacing: 0.5px;
      `;
      synthBtn.textContent = 'GENERATE ASSESSMENT';
      synthBtn.addEventListener('click', () => {
        this.assessmentGenerating = true;
        this.updateHeader();
        this.renderAssessmentSection();
        // Emit event for the orchestrator to handle synthesis
        if (this.investigation) {
          eventBus.emit('investigation:request', {
            query: '__synthesize:' + this.investigation.id,
            tier: this.investigation.tier,
          });
        }
      });
      right.appendChild(synthBtn);
    } else if (this.assessmentGenerating) {
      const generating = document.createElement('span');
      generating.style.cssText = 'font-size: 9px; color: #00b4d8; animation: graph-pulse 1.5s ease-in-out infinite;';
      generating.textContent = 'GENERATING...';
      right.appendChild(generating);
    } else if (this.draftAssessment) {
      const badge = document.createElement('span');
      badge.style.cssText = 'font-size: 9px; color: #30d158; font-weight: 600;';
      badge.textContent = '\u2713 ASSESSMENT';
      right.appendChild(badge);
    }

    // Export button
    if (this.investigation.state === 'review') {
      const exportBtn = document.createElement('button');
      exportBtn.style.cssText = `
        background: none; border: 1px solid #1e293b; color: #64748b;
        padding: 4px 10px; font-family: inherit; font-size: 9px;
        cursor: pointer; border-radius: 3px; letter-spacing: 0.5px;
      `;
      exportBtn.textContent = 'EXPORT';
      exportBtn.addEventListener('click', () => this.showExportMenu(exportBtn));
      right.appendChild(exportBtn);
    }

    const meta = document.createElement('span');
    meta.style.cssText = 'font-size: 9px; color: #334155;';
    meta.textContent = `${this.graphData.nodes.length} NODES \u00B7 ${this.graphData.links.length} EDGES \u00B7 TIER: ${this.investigation.tier}`;
    right.appendChild(meta);

    const time = document.createElement('span');
    time.style.cssText = 'font-size: 9px; color: #475569;';
    time.textContent = new Date(this.investigation.startedAt).toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
    right.appendChild(time);

    this.headerEl.appendChild(right);
  }

  // ---------------------------------------------------------------------------
  // Graph rendering — WP-S9.1
  // ---------------------------------------------------------------------------

  private buildGraphData(entities: Entity[], relationships: Relationship[]): GraphData {
    // Count relationships per entity for node sizing
    const relCounts = new Map<string, number>();
    for (const rel of relationships) {
      relCounts.set(rel.sourceEntityId, (relCounts.get(rel.sourceEntityId) ?? 0) + 1);
      relCounts.set(rel.targetEntityId, (relCounts.get(rel.targetEntityId) ?? 0) + 1);
    }

    const nodes: GraphNode[] = entities.map(e => ({
      id: e.id,
      label: e.name,
      type: e.type,
      entity: e,
      relationshipCount: relCounts.get(e.id) ?? 0,
    }));

    const nodeIds = new Set(nodes.map(n => n.id));
    const links: GraphLink[] = relationships
      .filter(r => nodeIds.has(r.sourceEntityId) && nodeIds.has(r.targetEntityId))
      .map(r => ({
        id: r.id,
        source: r.sourceEntityId as unknown as GraphNode,
        target: r.targetEntityId as unknown as GraphNode,
        label: r.type,
        confidence: r.confidence,
        relationship: r,
      }));

    return { nodes, links };
  }

  private renderGraph(): void {
    if (!this.svgContainer) return;

    // Remove old SVG and empty state
    if (this.svgElement) {
      this.svgElement.remove();
      this.svgElement = null;
    }
    const existingEmpty = this.svgContainer.querySelector('.graph-empty-state');
    if (existingEmpty) existingEmpty.remove();
    this.stopSimulation();

    // Zero entities — show clean message instead of empty graph
    if (this.graphData.nodes.length === 0) {
      const emptyEl = document.createElement('div');
      emptyEl.className = 'graph-empty-state';
      emptyEl.style.cssText = `
        position: absolute; inset: 0; display: flex; flex-direction: column;
        align-items: center; justify-content: center; color: #334155;
        font-family: 'JetBrains Mono', monospace;
      `;
      const icon = document.createElement('div');
      icon.style.cssText = 'font-size: 48px; opacity: 0.3; margin-bottom: 16px;';
      icon.textContent = '\u25CE';
      emptyEl.appendChild(icon);
      const msg = document.createElement('div');
      msg.style.cssText = 'font-size: 12px; letter-spacing: 1px; font-weight: 600;';
      msg.textContent = 'NO ENTITIES FOUND';
      emptyEl.appendChild(msg);
      const sub = document.createElement('div');
      sub.style.cssText = 'font-size: 10px; margin-top: 8px; max-width: 300px; text-align: center; line-height: 1.5;';
      sub.textContent = 'The investigation did not extract any entities from the collected sources. Check the collection log for details.';
      emptyEl.appendChild(sub);
      this.svgContainer.appendChild(emptyEl);
      return;
    }

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.style.cssText = 'width: 100%; height: 100%; position: absolute; inset: 0;';
    this.svgElement = svg;
    this.svgContainer.appendChild(svg);

    const svgSel = d3.select(svg);
    const w = this.svgContainer.clientWidth || 900;
    const h = this.svgContainer.clientHeight || 600;

    // Defs: glow filter
    const defs = svgSel.append('defs');
    const filter = defs.append('filter').attr('id', 'graph-glow');
    filter.append('feGaussianBlur').attr('stdDeviation', '4').attr('result', 'coloredBlur');
    const merge = filter.append('feMerge');
    merge.append('feMergeNode').attr('in', 'coloredBlur');
    merge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Root group for zoom/pan
    const g = svgSel.append('g');
    void g; // reference held via closure in simulation tick

    // Zoom behavior — WP-S9.2
    const zoomBehavior = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.2, 5])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });
    svgSel.call(zoomBehavior);

    // Links
    const linkGroup = g.append('g');
    const linkSel = linkGroup
      .selectAll<SVGLineElement, GraphLink>('line')
      .data(this.graphData.links, d => d.id)
      .enter()
      .append('line')
      .attr('data-link-id', d => d.id)
      .attr('data-link-src', d => d.relationship.sourceEntityId)
      .attr('data-link-tgt', d => d.relationship.targetEntityId)
      .attr('stroke', '#1e293b')
      .attr('stroke-width', d => Math.max(1, d.confidence * 2.5))
      .attr('stroke-opacity', 0.5)
      .attr('stroke-dasharray', d => d.confidence < 0.5 ? '4,4' : 'none');

    // Link labels
    const linkLabelGroup = g.append('g');
    const linkLabelSel = linkLabelGroup
      .selectAll<SVGTextElement, GraphLink>('text')
      .data(this.graphData.links, d => d.id)
      .enter()
      .append('text')
      .attr('data-link-id', d => `label-${d.id}`)
      .attr('data-link-src', d => d.relationship.sourceEntityId)
      .attr('data-link-tgt', d => d.relationship.targetEntityId)
      .text(d => d.label)
      .attr('font-size', '8px')
      .attr('fill', '#475569')
      .attr('text-anchor', 'middle')
      .attr('font-family', "'JetBrains Mono', monospace");

    // Weak match "?" badges — shown on dashed links that require analyst review
    const weakMatchPairKeys = new Set(
      this.weakMatches.map(m => `${m.entityA.id}:${m.entityB.id}`),
    );
    const weakLinks = this.graphData.links.filter(l => {
      const srcId = l.relationship.sourceEntityId;
      const tgtId = l.relationship.targetEntityId;
      return weakMatchPairKeys.has(`${srcId}:${tgtId}`) || weakMatchPairKeys.has(`${tgtId}:${srcId}`);
    });

    if (weakLinks.length > 0) {
      const badgeGroup = g.append('g').attr('class', 'weak-match-badges');
      const badgeSel = badgeGroup
        .selectAll<SVGGElement, GraphLink>('g')
        .data(weakLinks, d => d.id)
        .enter()
        .append('g')
        .style('cursor', 'pointer')
        .on('click', (_event, d) => {
          const match = this.weakMatches.find(m => {
            const srcId = d.relationship.sourceEntityId;
            const tgtId = d.relationship.targetEntityId;
            return (m.entityA.id === srcId && m.entityB.id === tgtId)
              || (m.entityA.id === tgtId && m.entityB.id === srcId);
          });
          if (match) this.showMergeDialog(match);
        });

      badgeSel.append('circle')
        .attr('r', 8)
        .attr('fill', '#f59e0b')
        .attr('stroke', '#080b12')
        .attr('stroke-width', 1.5);

      badgeSel.append('text')
        .text('?')
        .attr('text-anchor', 'middle')
        .attr('dy', '0.35em')
        .attr('font-size', '10px')
        .attr('font-weight', 'bold')
        .attr('fill', '#080b12')
        .attr('font-family', "'JetBrains Mono', monospace");

      // Position badges at link midpoints during tick
      this.cleanupFns.push(() => { /* badge positioning handled in tick */ });

      // Store badge selection for tick updates
      (this as any)._weakBadgeSel = badgeSel;
    }

    // Nodes
    const nodeGroup = g.append('g');
    const nodeSel = nodeGroup
      .selectAll<SVGGElement, GraphNode>('g')
      .data(this.graphData.nodes, d => d.id)
      .enter()
      .append('g')
      .attr('data-node-id', d => d.id)
      .style('cursor', 'pointer');

    // Drag behavior — WP-S9.2
    const dragBehavior = d3.drag<SVGGElement, GraphNode>()
      .on('start', (event, d) => {
        if (!event.active && this.simulation) this.simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      })
      .on('drag', (event, d) => {
        d.fx = event.x;
        d.fy = event.y;
      })
      .on('end', (event, d) => {
        if (!event.active && this.simulation) this.simulation.alphaTarget(0);
        d.fx = null;
        d.fy = null;
      });
    nodeSel.call(dragBehavior);

    // Click node — WP-S9.2
    nodeSel.on('click', (event, d) => {
      event.stopPropagation();
      this.selectedNode = d;
      this.renderNodeDetail();
    });

    // Double-click — center and zoom to node
    nodeSel.on('dblclick', (event, d) => {
      event.stopPropagation();
      const scale = 1.5;
      const tx = w / 2 - (d.x ?? 0) * scale;
      const ty = h / 2 - (d.y ?? 0) * scale;
      svgSel.transition().duration(500).call(
        zoomBehavior.transform,
        d3.zoomIdentity.translate(tx, ty).scale(scale),
      );
    });

    // Right-click context menu — WP-S9.2
    nodeSel.on('contextmenu', (event, d) => {
      event.preventDefault();
      event.stopPropagation();
      this.showContextMenu(event.clientX, event.clientY, d);
    });

    // Outer ring (type-colored)
    nodeSel
      .append('circle')
      .attr('r', d => this.getNodeRadius(d))
      .attr('fill', d => TYPE_COLORS[d.type] + '15')
      .attr('stroke', d => TYPE_COLORS[d.type])
      .attr('stroke-width', d => {
        // Single-source: dashed border, lower opacity
        const corrob = d.entity.corroboration;
        return corrob.count <= 1 ? 1 : 1.5;
      })
      .attr('stroke-dasharray', d => {
        return d.entity.corroboration.count <= 1 ? '3,3' : 'none';
      })
      .attr('opacity', d => d.entity.corroboration.count <= 1 ? 0.6 : 1)
      .attr('filter', 'url(#graph-glow)');

    // Inner dot
    nodeSel
      .append('circle')
      .attr('r', 4)
      .attr('fill', d => TYPE_COLORS[d.type]);

    // NATO rating badge (small text)
    nodeSel
      .append('text')
      .text(d => d.entity.corroboration.natoRating ?? '')
      .attr('text-anchor', 'middle')
      .attr('dy', d => -(this.getNodeRadius(d) + 6))
      .attr('font-size', '7px')
      .attr('fill', '#94a3b8')
      .attr('font-family', "'JetBrains Mono', monospace")
      .attr('font-weight', '600');

    // Node label
    nodeSel
      .append('text')
      .text(d => d.label)
      .attr('text-anchor', 'middle')
      .attr('dy', d => this.getNodeRadius(d) + 14)
      .attr('font-size', '9px')
      .attr('fill', '#94a3b8')
      .attr('font-family', "'JetBrains Mono', monospace")
      .attr('font-weight', '500');

    // Click on background to deselect
    svgSel.on('click', () => {
      this.selectedNode = null;
      this.renderNodeDetail();
    });

    // Force simulation
    this.simulation = d3.forceSimulation<GraphNode>(this.graphData.nodes)
      .force('link', d3.forceLink<GraphNode, GraphLink>(this.graphData.links).id(d => d.id).distance(130))
      .force('charge', d3.forceManyBody().strength(-500))
      .force('center', d3.forceCenter(w / 2, h / 2))
      .force('collision', d3.forceCollide().radius(45))
      .on('tick', () => {
        linkSel
          .attr('x1', d => (d.source as GraphNode).x ?? 0)
          .attr('y1', d => (d.source as GraphNode).y ?? 0)
          .attr('x2', d => (d.target as GraphNode).x ?? 0)
          .attr('y2', d => (d.target as GraphNode).y ?? 0);

        linkLabelSel
          .attr('x', d => (((d.source as GraphNode).x ?? 0) + ((d.target as GraphNode).x ?? 0)) / 2)
          .attr('y', d => (((d.source as GraphNode).y ?? 0) + ((d.target as GraphNode).y ?? 0)) / 2 - 5);

        nodeSel.attr('transform', d => `translate(${d.x ?? 0},${d.y ?? 0})`);

        // Position weak match badges at link midpoints
        const weakBadges = (this as any)._weakBadgeSel;
        if (weakBadges) {
          weakBadges.attr('transform', (d: GraphLink) => {
            const sx = (d.source as GraphNode).x ?? 0;
            const sy = (d.source as GraphNode).y ?? 0;
            const tx = (d.target as GraphNode).x ?? 0;
            const ty = (d.target as GraphNode).y ?? 0;
            return `translate(${(sx + tx) / 2},${(sy + ty) / 2})`;
          });
        }
      });

    // Initial zoom fit
    setTimeout(() => {
      svgSel.transition().duration(600).call(
        zoomBehavior.transform,
        d3.zoomIdentity.scale(0.85).translate(w * 0.09, h * 0.09),
      );
    }, 400);

    // Entity type legend — WP-S9.2
    this.renderLegend();

    // Confidence filter — WP-S11.4
    this.renderConfidenceFilter();
  }

  private getNodeRadius(d: GraphNode): number {
    // Base radius + relationship count scaling
    const base = d.type === 'EVENT' ? 24 : 18;
    const bonus = Math.min(d.relationshipCount * 2, 12);
    return base + bonus;
  }

  /** Re-render graph when nodes/links change incrementally. */
  private refreshGraph(): void {
    // Full re-render for simplicity — D3 handles enter/exit
    this.renderGraph();
    this.updateHeader();
  }

  private stopSimulation(): void {
    if (this.simulation) {
      this.simulation.stop();
      this.simulation = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Legend — WP-S9.2
  // ---------------------------------------------------------------------------

  private renderLegend(): void {
    if (!this.svgContainer) return;

    // Remove existing legend
    const existing = this.svgContainer.querySelector('.graph-legend');
    if (existing) existing.remove();

    const legend = document.createElement('div');
    legend.className = 'graph-legend';
    legend.style.cssText = `
      position: absolute; bottom: 16px; left: 16px;
      display: flex; gap: 12px; padding: 8px 12px;
      background: #0a0e17cc; border: 1px solid #1e293b; border-radius: 4px;
    `;

    for (const [type, color] of Object.entries(TYPE_COLORS)) {
      const item = document.createElement('div');
      item.style.cssText = 'display: flex; align-items: center; gap: 4px;';

      const dot = document.createElement('div');
      dot.style.cssText = `width: 6px; height: 6px; border-radius: 50%; background: ${color};`;
      item.appendChild(dot);

      const label = document.createElement('span');
      label.style.cssText = 'font-size: 8px; color: #475569; text-transform: uppercase; letter-spacing: 0.5px;';
      label.textContent = type.toLowerCase();
      item.appendChild(label);

      legend.appendChild(item);
    }

    this.svgContainer.appendChild(legend);
  }

  // ---------------------------------------------------------------------------
  // Confidence filter — WP-S11.4
  // ---------------------------------------------------------------------------

  private renderConfidenceFilter(): void {
    if (!this.svgContainer) return;

    // Remove existing filter
    const existing = this.svgContainer.querySelector('.graph-confidence-filter');
    if (existing) existing.remove();

    const filterEl = document.createElement('div');
    filterEl.className = 'graph-confidence-filter';
    filterEl.style.cssText = `
      position: absolute; bottom: 16px; right: 296px;
      display: flex; align-items: center; gap: 6px; padding: 6px 12px;
      background: #0a0e17cc; border: 1px solid #1e293b; border-radius: 4px;
    `;

    const label = document.createElement('span');
    label.style.cssText = 'font-size: 8px; color: #475569; letter-spacing: 0.5px;';
    label.textContent = 'MIN CREDIBILITY:';
    filterEl.appendChild(label);

    // Credibility buttons 1-6 + ALL
    const levels: Array<{ label: string; value: number }> = [
      { label: '1', value: 1 },
      { label: '2', value: 2 },
      { label: '3', value: 3 },
      { label: '4', value: 4 },
      { label: '5', value: 5 },
      { label: 'ALL', value: 6 },
    ];

    for (const level of levels) {
      const btn = document.createElement('button');
      const isActive = this.minCredibility === level.value;
      btn.style.cssText = `
        padding: 2px 6px; font-size: 8px; font-family: inherit; font-weight: 600;
        border: 1px solid ${isActive ? '#00b4d8' : '#1e293b'};
        border-radius: 2px; cursor: pointer;
        background: ${isActive ? '#00b4d822' : 'transparent'};
        color: ${isActive ? '#00b4d8' : '#475569'};
      `;
      btn.textContent = level.label;

      btn.addEventListener('click', () => {
        this.minCredibility = level.value;
        this.applyConfidenceFilter();
        this.renderConfidenceFilter();
      });

      filterEl.appendChild(btn);
    }

    this.svgContainer.appendChild(filterEl);
  }

  /**
   * Apply confidence filter to the graph.
   * Hides nodes with credibility rating worse (higher number) than the threshold.
   * Credibility is the second character of the NATO rating (e.g., "B2" → 2).
   */
  private applyConfidenceFilter(): void {
    const minCred = this.minCredibility;

    // Determine which node IDs pass the filter
    const visibleNodeIds = new Set<string>();
    for (const node of this.graphData.nodes) {
      const rating = node.entity.corroboration.natoRating;
      const credibility = parseInt(rating.charAt(1), 10);
      if (isNaN(credibility) || credibility <= minCred) {
        visibleNodeIds.add(node.id);
      }
    }

    // Apply opacity to nodes via data attribute
    if (!this.svgElement) return;
    const nodeGroups = this.svgElement.querySelectorAll('[data-node-id]');
    for (const el of nodeGroups) {
      const nodeId = el.getAttribute('data-node-id');
      const visible = nodeId !== null && visibleNodeIds.has(nodeId);
      (el as SVGGElement).style.opacity = visible ? '' : '0.08';
      (el as SVGGElement).style.pointerEvents = visible ? '' : 'none';
    }

    // Apply opacity to links via data attribute
    const linkEls = this.svgElement.querySelectorAll('[data-link-id]');
    for (const el of linkEls) {
      const srcId = el.getAttribute('data-link-src');
      const tgtId = el.getAttribute('data-link-tgt');
      const visible = srcId !== null && tgtId !== null &&
        visibleNodeIds.has(srcId) && visibleNodeIds.has(tgtId);
      (el as SVGElement).style.opacity = visible ? '' : '0.05';
    }
  }

  // ---------------------------------------------------------------------------
  // Context menu — WP-S9.2
  // ---------------------------------------------------------------------------

  private showContextMenu(x: number, y: number, node: GraphNode): void {
    // Remove existing
    this.removeContextMenu();

    const menu = document.createElement('div');
    menu.className = 'graph-context-menu';
    menu.style.cssText = `
      position: fixed; left: ${x}px; top: ${y}px; z-index: 10001;
      background: #0a0e17; border: 1px solid #1e293b; border-radius: 4px;
      padding: 4px 0; min-width: 160px; box-shadow: 0 4px 12px rgba(0,0,0,0.5);
    `;

    const items = [
      { label: 'Watch Entity', action: () => this.setWatchStatus(node, 'watched') },
      { label: 'Priority Watch', action: () => this.setWatchStatus(node, 'priority') },
      { label: 'Add Note', action: () => this.promptNote(node) },
      { label: 'Remove from Graph', action: () => this.removeNode(node) },
      { label: 'Investigate Further', action: () => this.investigateFurther(node) },
    ];

    for (const item of items) {
      const el = document.createElement('div');
      el.style.cssText = `
        padding: 6px 14px; font-size: 10px; color: #94a3b8; cursor: pointer;
        font-family: 'JetBrains Mono', monospace;
      `;
      el.textContent = item.label;
      el.addEventListener('mouseenter', () => { el.style.background = '#1e293b'; });
      el.addEventListener('mouseleave', () => { el.style.background = ''; });
      el.addEventListener('click', () => {
        item.action();
        this.removeContextMenu();
      });
      menu.appendChild(el);
    }

    document.body.appendChild(menu);

    // Close on click elsewhere
    const closeHandler = () => {
      this.removeContextMenu();
      document.removeEventListener('click', closeHandler);
    };
    setTimeout(() => document.addEventListener('click', closeHandler), 0);
  }

  private removeContextMenu(): void {
    const existing = document.querySelector('.graph-context-menu');
    if (existing) existing.remove();
  }

  private setWatchStatus(node: GraphNode, status: 'watched' | 'priority'): void {
    node.entity.watchStatus = status;
    eventBus.emit('entity:updated', node.entity);
  }

  private async promptNote(node: GraphNode): Promise<void> {
    const note = prompt(`Add note to "${node.label}":`);
    if (note && note.trim()) {
      const text = note.trim();
      // Persist to SQLite
      const { addEntityNote } = await import('@/intelligence/entities/entity-store');
      await addEntityNote(node.entity.id, text);
      // Update in-memory for display
      node.entity.notes.push(text);
      eventBus.emit('entity:updated', node.entity);
      if (this.selectedNode?.id === node.id) {
        this.renderNodeDetail();
      }
    }
  }

  private removeNode(node: GraphNode): void {
    this.graphData.nodes = this.graphData.nodes.filter(n => n.id !== node.id);
    this.graphData.links = this.graphData.links.filter(l => {
      const src = typeof l.source === 'string' ? l.source : (l.source as GraphNode).id;
      const tgt = typeof l.target === 'string' ? l.target : (l.target as GraphNode).id;
      return src !== node.id && tgt !== node.id;
    });
    if (this.selectedNode?.id === node.id) {
      this.selectedNode = null;
    }
    this.refreshGraph();
    this.renderSidebar();
  }

  private investigateFurther(node: GraphNode): void {
    // Emit event for the investigation UI to pick up
    eventBus.emit('entity:selected', node.entity);
  }

  // ---------------------------------------------------------------------------
  // Sidebar — WP-S9.3, WP-S9.4
  // ---------------------------------------------------------------------------

  private renderSidebar(): void {
    if (!this.sidebarContainer) return;
    this.sidebarContainer.innerHTML = '';

    // Node detail section (top)
    const detailSection = document.createElement('div');
    detailSection.className = 'graph-node-detail';
    detailSection.style.cssText = 'border-bottom: 1px solid #1e293b;';
    this.sidebarContainer.appendChild(detailSection);

    // Scrollable bottom section: collection log + coverage
    const scrollSection = document.createElement('div');
    scrollSection.className = 'graph-sidebar-scroll';
    scrollSection.style.cssText = 'flex: 1; overflow-y: auto;';
    this.sidebarContainer.appendChild(scrollSection);

    this.renderNodeDetail();
    this.renderAssessmentSection();
    this.renderCollectionLog();
    this.renderCoverageSection();
  }

  // ---------------------------------------------------------------------------
  // Node detail — WP-S9.3
  // ---------------------------------------------------------------------------

  private renderNodeDetail(): void {
    const section = this.sidebarContainer?.querySelector('.graph-node-detail');
    if (!section) return;
    section.innerHTML = '';

    // Section header
    const header = document.createElement('div');
    header.style.cssText = 'padding: 10px 14px; border-bottom: 1px solid #1e293b;';
    const headerLabel = document.createElement('span');
    headerLabel.style.cssText = 'font-size: 9px; font-weight: 600; letter-spacing: 2px; color: #475569;';
    headerLabel.textContent = 'NODE DETAILS';
    header.appendChild(headerLabel);
    section.appendChild(header);

    if (!this.selectedNode) {
      const empty = document.createElement('div');
      empty.style.cssText = 'padding: 20px 14px; font-size: 10px; color: #1e293b; text-align: center;';
      empty.textContent = 'Click a node to inspect';
      section.appendChild(empty);
      return;
    }

    const node = this.selectedNode;
    const entity = node.entity;
    const content = document.createElement('div');
    content.style.cssText = 'padding: 14px;';

    // Name with type dot
    const nameRow = document.createElement('div');
    nameRow.style.cssText = 'display: flex; align-items: center; gap: 8px; margin-bottom: 14px;';
    const typeDot = document.createElement('div');
    typeDot.style.cssText = `
      width: 8px; height: 8px; border-radius: 50%;
      background: ${TYPE_COLORS[entity.type]};
      box-shadow: 0 0 8px ${TYPE_COLORS[entity.type]}66;
    `;
    nameRow.appendChild(typeDot);
    const nameLabel = document.createElement('span');
    nameLabel.style.cssText = 'font-size: 12px; font-weight: 600; color: #e2e8f0;';
    nameLabel.textContent = entity.name;
    nameRow.appendChild(nameLabel);
    content.appendChild(nameRow);

    // TYPE
    content.appendChild(this.createDetailField('TYPE', TYPE_LABELS[entity.type] ?? entity.type, TYPE_COLORS[entity.type]));

    // NATO RATING
    const rating = entity.corroboration.natoRating;
    const reliabilityChar = rating.charAt(0);
    const reliabilityLabel = RELIABILITY_LABELS[reliabilityChar] ?? '';
    content.appendChild(this.createDetailField(
      'NATO RATING',
      `${rating} \u2014 ${reliabilityLabel}`,
    ));

    // SOURCES
    const sources = entity.corroboration.sources.length > 0
      ? entity.corroboration.sources.join(', ')
      : entity.provenance.sourceUrl || 'Unknown';
    content.appendChild(this.createDetailField('SOURCES', sources));

    // PROVENANCE
    if (entity.provenance.extractionQuote) {
      content.appendChild(this.createDetailField(
        'EXTRACTION QUOTE',
        `"${entity.provenance.extractionQuote}"`,
      ));
    }
    if (entity.provenance.sourceUrl) {
      const urlField = this.createDetailField('SOURCE URL', '');
      const urlLink = document.createElement('a');
      urlLink.href = entity.provenance.sourceUrl;
      urlLink.target = '_blank';
      urlLink.rel = 'noopener noreferrer';
      urlLink.style.cssText = 'font-size: 10px; color: #00b4d8; word-break: break-all; text-decoration: none;';
      urlLink.textContent = entity.provenance.sourceUrl;
      const valueEl = urlField.querySelector('.detail-value');
      if (valueEl) {
        valueEl.textContent = '';
        valueEl.appendChild(urlLink);
      }
      content.appendChild(urlField);
    }

    // CONFIDENCE
    content.appendChild(this.createDetailField(
      'CONFIDENCE',
      `${(entity.confidence * 100).toFixed(0)}%`,
    ));

    // Buttons
    const expandBtn = this.createSidebarButton('EXPAND CONNECTIONS', '#8338ec');
    expandBtn.addEventListener('click', () => {
      // Future: expand neighborhood in graph
    });
    content.appendChild(expandBtn);

    const investigateBtn = this.createSidebarButton('DEEP INVESTIGATE', '#00b4d8');
    investigateBtn.addEventListener('click', () => {
      this.investigateFurther(node);
    });
    content.appendChild(investigateBtn);

    // Notes section — WP-S15.4
    const notesSection = document.createElement('div');
    notesSection.style.cssText = 'margin-top: 10px; padding-top: 8px; border-top: 1px solid #1e293b;';

    const notesLabel = document.createElement('div');
    notesLabel.style.cssText = 'font-size: 8px; color: #334155; letter-spacing: 1px; margin-bottom: 4px;';
    notesLabel.textContent = 'NOTES';
    notesSection.appendChild(notesLabel);

    // Existing notes
    const notesList = document.createElement('div');
    notesList.className = 'entity-notes-list';
    if (entity.notes.length > 0) {
      for (const noteText of entity.notes) {
        const noteEl = document.createElement('div');
        noteEl.style.cssText = 'font-size: 9px; color: #94a3b8; padding: 3px 0; border-bottom: 1px solid #0d1117;';
        noteEl.textContent = noteText;
        notesList.appendChild(noteEl);
      }
    }
    notesSection.appendChild(notesList);

    // Add note input
    const addRow = document.createElement('div');
    addRow.style.cssText = 'display: flex; gap: 4px; margin-top: 4px;';

    const noteInput = document.createElement('input');
    noteInput.type = 'text';
    noteInput.placeholder = 'Add note...';
    noteInput.style.cssText = `
      flex: 1; background: #080b12; border: 1px solid #1e293b; border-radius: 3px;
      padding: 4px 6px; font-size: 8px; font-family: inherit; color: #e2e8f0; outline: none;
    `;

    const addBtn = document.createElement('button');
    addBtn.style.cssText = `
      background: none; border: 1px solid #1e293b; color: #00b4d8;
      padding: 4px 8px; font-size: 8px; font-family: inherit;
      cursor: pointer; border-radius: 3px;
    `;
    addBtn.textContent = '+';
    addBtn.addEventListener('click', async () => {
      const text = noteInput.value.trim();
      if (!text) return;

      const { addEntityNote } = await import('@/intelligence/entities/entity-store');
      await addEntityNote(entity.id, text);

      // Update the entity's local notes array for display
      entity.notes.push(text);
      noteInput.value = '';

      // Re-render the notes list
      const newNoteEl = document.createElement('div');
      newNoteEl.style.cssText = 'font-size: 9px; color: #94a3b8; padding: 3px 0; border-bottom: 1px solid #0d1117;';
      newNoteEl.textContent = text;
      notesList.appendChild(newNoteEl);
    });

    noteInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') addBtn.click();
    });

    addRow.appendChild(noteInput);
    addRow.appendChild(addBtn);
    notesSection.appendChild(addRow);

    content.appendChild(notesSection);

    section.appendChild(content);
  }

  private createDetailField(label: string, value: string, valueColor?: string): HTMLElement {
    const field = document.createElement('div');
    field.style.cssText = 'margin-bottom: 10px;';

    const labelEl = document.createElement('div');
    labelEl.style.cssText = 'font-size: 8px; color: #334155; letter-spacing: 1px; margin-bottom: 3px;';
    labelEl.textContent = label;
    field.appendChild(labelEl);

    const valueEl = document.createElement('div');
    valueEl.className = 'detail-value';
    valueEl.style.cssText = `font-size: 10px; color: ${valueColor ?? '#94a3b8'};`;
    if (value.startsWith('"') && value.endsWith('"')) {
      valueEl.style.fontStyle = 'italic';
    }
    valueEl.textContent = value;
    field.appendChild(valueEl);

    return field;
  }

  private createSidebarButton(text: string, color: string): HTMLElement {
    const btn = document.createElement('button');
    btn.style.cssText = `
      width: 100%; padding: 7px; font-size: 9px; font-family: inherit;
      font-weight: 600; letter-spacing: 1px; border: 1px solid #1e293b;
      border-radius: 3px; background: transparent; color: ${color};
      cursor: pointer; margin-bottom: 5px;
    `;
    btn.textContent = text;
    btn.addEventListener('mouseenter', () => { btn.style.background = `${color}18`; });
    btn.addEventListener('mouseleave', () => { btn.style.background = 'transparent'; });
    return btn;
  }

  // ---------------------------------------------------------------------------
  // Collection log — WP-S9.4
  // ---------------------------------------------------------------------------

  private renderCollectionLog(): void {
    const scroll = this.sidebarContainer?.querySelector('.graph-sidebar-scroll');
    if (!scroll) return;

    // Remove existing log
    const existing = scroll.querySelector('.graph-collection-log');
    if (existing) existing.remove();

    const logSection = document.createElement('div');
    logSection.className = 'graph-collection-log';

    // Header
    const header = document.createElement('div');
    header.style.cssText = 'padding: 10px 14px; border-bottom: 1px solid #1e293b;';
    const headerLabel = document.createElement('span');
    headerLabel.style.cssText = 'font-size: 9px; font-weight: 600; letter-spacing: 2px; color: #475569;';
    headerLabel.textContent = 'COLLECTION LOG';
    header.appendChild(headerLabel);
    logSection.appendChild(header);

    // Phase checklist
    const phases: { state: InvestigationState; label: string }[] = [
      { state: 'decompose', label: 'Decomposing query...' },
      { state: 'collect', label: 'Collecting from sources...' },
      { state: 'extract', label: 'Extracting entities...' },
      { state: 'validate', label: 'Validating extractions...' },
      { state: 'graph_build', label: 'Building graph...' },
      { state: 'corroborate', label: 'Corroboration pass...' },
      { state: 'synthesize', label: 'Generating synthesis...' },
      { state: 'review', label: 'Complete' },
    ];

    const list = document.createElement('div');
    list.style.cssText = 'padding: 8px 14px;';

    for (const phase of phases) {
      const row = document.createElement('div');
      row.style.cssText = 'display: flex; align-items: center; gap: 6px; padding: 3px 0; font-size: 9px;';

      const isCompleted = this.completedPhases.has(phase.state);
      const isCurrent = this.investigation?.state === phase.state;

      row.style.color = isCompleted ? '#30d158' : isCurrent ? '#00b4d8' : '#1e293b';

      const icon = document.createElement('span');
      icon.style.cssText = 'width: 10px;';
      icon.textContent = isCompleted ? '\u2713' : isCurrent ? '\u25B8' : '\u25CB';
      row.appendChild(icon);

      const label = document.createElement('span');
      label.textContent = phase.label;
      if (isCurrent) {
        label.style.animation = 'graph-pulse 1.5s ease-in-out infinite';
      }
      row.appendChild(label);

      list.appendChild(row);
    }

    logSection.appendChild(list);
    scroll.appendChild(logSection);
  }

  // ---------------------------------------------------------------------------
  // Coverage section — WP-S9.4
  // ---------------------------------------------------------------------------

  private renderCoverageSection(): void {
    const scroll = this.sidebarContainer?.querySelector('.graph-sidebar-scroll');
    if (!scroll) return;

    // Remove existing
    const existing = scroll.querySelector('.graph-coverage');
    if (existing) existing.remove();

    const coverageEl = document.createElement('div');
    coverageEl.className = 'graph-coverage';
    coverageEl.style.cssText = `
      margin: 12px 14px; padding: 8px; background: #0d1117;
      border: 1px solid #1e293b; border-radius: 3px;
    `;

    // Header
    const header = document.createElement('div');
    header.style.cssText = 'font-size: 8px; color: #334155; letter-spacing: 1px; margin-bottom: 6px;';
    header.textContent = 'COVERAGE';
    coverageEl.appendChild(header);

    if (this.coverageReport) {
      const body = document.createElement('div');
      body.style.cssText = 'font-size: 9px; color: #64748b; line-height: 1.6;';

      for (const param of this.coverageReport.parameters) {
        const line = document.createElement('div');
        const statusColor = param.corroborated ? '#30d158' : param.addressed ? '#ffaa00' : '#64748b';
        const statusText = param.corroborated ? 'YES' : param.addressed ? 'PARTIAL' : 'NO';
        line.innerHTML = `${escapeHtml(param.name)}: <span style="color: ${statusColor}">${statusText}</span>`;
        body.appendChild(line);
      }

      // Overall coverage
      const overallLine = document.createElement('div');
      const pct = this.coverageReport.overallPercentage;
      const pctColor = pct >= 80 ? '#30d158' : pct >= 50 ? '#ffaa00' : '#ff4444';
      overallLine.innerHTML = `Coverage: <span style="color: ${pctColor}">${pct}%</span>`;
      body.appendChild(overallLine);

      coverageEl.appendChild(body);
    } else {
      const empty = document.createElement('div');
      empty.style.cssText = 'font-size: 9px; color: #1e293b;';
      empty.textContent = 'No coverage data yet';
      coverageEl.appendChild(empty);
    }

    scroll.appendChild(coverageEl);
  }

  // ---------------------------------------------------------------------------
  // Assessment section — WP-S15.5
  // ---------------------------------------------------------------------------

  private renderAssessmentSection(): void {
    const scroll = this.sidebarContainer?.querySelector('.graph-sidebar-scroll');
    if (!scroll) return;

    // Remove existing assessment section
    const existing = scroll.querySelector('.graph-assessment');
    if (existing) existing.remove();

    const section = document.createElement('div');
    section.className = 'graph-assessment';

    // Header
    const header = document.createElement('div');
    header.style.cssText = 'padding: 10px 14px; border-bottom: 1px solid #1e293b; border-top: 1px solid #1e293b;';
    const headerLabel = document.createElement('span');
    headerLabel.style.cssText = 'font-size: 9px; font-weight: 600; letter-spacing: 2px; color: #475569;';
    headerLabel.textContent = 'ASSESSMENT';
    header.appendChild(headerLabel);
    section.appendChild(header);

    if (this.assessmentGenerating) {
      const loading = document.createElement('div');
      loading.style.cssText = 'padding: 14px; font-size: 9px; color: #00b4d8; animation: graph-pulse 1.5s ease-in-out infinite;';
      loading.textContent = 'Generating draft assessment...';
      section.appendChild(loading);
    } else if (this.draftAssessment) {
      const body = document.createElement('div');
      body.style.cssText = 'padding: 14px;';

      // Machine-generated label
      const label = document.createElement('div');
      label.style.cssText = 'font-size: 7px; color: #ffaa00; letter-spacing: 1px; margin-bottom: 8px; font-weight: 600;';
      label.textContent = 'MACHINE-GENERATED DRAFT \u2014 REQUIRES ANALYST REVIEW';
      body.appendChild(label);

      // Executive summary
      const execHeader = document.createElement('div');
      execHeader.style.cssText = 'font-size: 8px; color: #334155; letter-spacing: 1px; margin-bottom: 4px;';
      execHeader.textContent = 'EXECUTIVE SUMMARY';
      body.appendChild(execHeader);

      const execBody = document.createElement('div');
      execBody.style.cssText = 'font-size: 9px; color: #94a3b8; line-height: 1.5; margin-bottom: 12px;';
      execBody.textContent = this.draftAssessment.executiveSummary;
      body.appendChild(execBody);

      // Sections
      for (const s of this.draftAssessment.sections) {
        const sectionHeader = document.createElement('div');
        sectionHeader.style.cssText = 'font-size: 8px; color: #00b4d8; letter-spacing: 1px; margin-bottom: 4px; font-weight: 600;';
        sectionHeader.textContent = s.title.toUpperCase();
        body.appendChild(sectionHeader);

        const sectionBody = document.createElement('div');
        sectionBody.style.cssText = 'font-size: 9px; color: #94a3b8; line-height: 1.5; margin-bottom: 8px;';
        sectionBody.textContent = s.content;
        body.appendChild(sectionBody);

        if (s.citations.length > 0) {
          const citeList = document.createElement('div');
          citeList.style.cssText = 'font-size: 7px; color: #475569; margin-bottom: 10px;';
          citeList.textContent = `Refs: ${s.citations.join(', ')}`;
          body.appendChild(citeList);
        }
      }

      // Model info
      const modelInfo = document.createElement('div');
      modelInfo.style.cssText = 'font-size: 7px; color: #334155; margin-top: 8px; padding-top: 8px; border-top: 1px solid #1e293b;';
      modelInfo.textContent = `Model: ${this.draftAssessment.modelUsed} | Tokens: ${this.draftAssessment.tokenCost} | ${new Date(this.draftAssessment.generatedAt).toLocaleTimeString()}`;
      body.appendChild(modelInfo);

      section.appendChild(body);
    }

    // Insert before the collection log section
    const collectionLog = scroll.querySelector('.graph-collection-log');
    if (collectionLog) {
      scroll.insertBefore(section, collectionLog);
    } else {
      scroll.appendChild(section);
    }
  }

  // ---------------------------------------------------------------------------
  // Export menu — WP-S15.5
  // ---------------------------------------------------------------------------

  private showExportMenu(anchor: HTMLElement): void {
    // Remove existing menu
    const existing = this.container.querySelector('.graph-export-menu');
    if (existing) { existing.remove(); return; }

    const menu = document.createElement('div');
    menu.className = 'graph-export-menu';
    const rect = anchor.getBoundingClientRect();
    menu.style.cssText = `
      position: fixed; top: ${rect.bottom + 4}px; right: ${window.innerWidth - rect.right}px;
      background: #0e1219; border: 1px solid #1e293b; border-radius: 4px;
      padding: 4px 0; z-index: 20000; min-width: 140px;
    `;

    const formats = [
      { id: 'json', label: 'JSON (Full)' },
      { id: 'csv', label: 'CSV (Tables)' },
      { id: 'markdown', label: 'Markdown' },
      { id: 'stix', label: 'STIX 2.1' },
    ];

    for (const fmt of formats) {
      const item = document.createElement('div');
      item.style.cssText = `
        padding: 6px 14px; font-size: 9px; color: #94a3b8; cursor: pointer;
      `;
      item.textContent = fmt.label;
      item.addEventListener('mouseenter', () => { item.style.background = '#1e293b'; });
      item.addEventListener('mouseleave', () => { item.style.background = 'none'; });
      item.addEventListener('click', () => {
        menu.remove();
        this.exportInvestigation(fmt.id);
      });
      menu.appendChild(item);
    }

    this.container.appendChild(menu);

    // Close on click outside
    const closeHandler = (e: MouseEvent) => {
      if (!menu.contains(e.target as Node) && e.target !== anchor) {
        menu.remove();
        document.removeEventListener('click', closeHandler);
      }
    };
    setTimeout(() => document.addEventListener('click', closeHandler), 0);
  }

  private async exportInvestigation(formatId: string): Promise<void> {
    if (!this.investigation) return;

    const { exportInvestigation } = await import('@/intelligence/reporting/export');
    const { getCollectionLogSummary } = await import('@/intelligence/investigation/collection-log');

    let auditLog = null;
    try {
      auditLog = await getCollectionLogSummary(this.investigation.id);
    } catch {
      // Audit log is optional for export
    }

    const entities = this.graphData.nodes.map(n => n.entity);
    const relationships = this.graphData.links.map(l => l.relationship);

    const blob = exportInvestigation({
      investigationId: this.investigation.id,
      title: this.investigation.title,
      query: this.investigation.rawQuery,
      tier: this.investigation.tier,
      deskId: this.investigation.deskId,
      entities,
      relationships,
      assessment: this.draftAssessment,
      auditLog,
      exportedAt: new Date().toISOString(),
    }, formatId);

    // Trigger download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ext = formatId === 'stix' ? '.stix.json' : formatId === 'json' ? '.json' : formatId === 'csv' ? '.csv' : '.md';
    a.download = `investigation-${this.investigation.id.slice(0, 8)}${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ---------------------------------------------------------------------------
  // Status bar — WP-S9.4
  // ---------------------------------------------------------------------------

  private updateStatusBar(): void {
    if (!this.statusBar) return;
    this.statusBar.innerHTML = '';

    const left = document.createElement('div');
    left.style.cssText = 'display: flex; gap: 20px;';

    if (this.resourceState) {
      const rs = this.resourceState;
      left.appendChild(this.createStatusItem(`TOKENS: ${this.formatNumber(rs.tokensUsed)}/${this.formatBudget(rs.tokenBudget)}`));
      left.appendChild(this.createStatusItem(`TIME: ${rs.elapsedSeconds}s/${rs.timeLimitSeconds}s`));
      left.appendChild(this.createStatusItem(`SUFFICIENCY: ${rs.coveragePercent}%`));
    } else {
      left.appendChild(this.createStatusItem(`NODES: ${this.graphData.nodes.length}`));
      left.appendChild(this.createStatusItem(`EDGES: ${this.graphData.links.length}`));
    }

    this.statusBar.appendChild(left);

    const right = document.createElement('span');
    right.style.cssText = 'font-size: 8px; color: #334155;';
    right.textContent = 'SENTINEL v0.2.0 \u2014 INVESTIGATION VIEW';
    this.statusBar.appendChild(right);
  }

  private createStatusItem(text: string): HTMLElement {
    const el = document.createElement('span');
    el.style.cssText = 'font-size: 8px; color: #64748b;';
    el.textContent = text;
    return el;
  }

  private formatNumber(n: number): string {
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return String(n);
  }

  private formatBudget(n: number): string {
    if (n >= 1000) return (n / 1000).toFixed(0) + 'K';
    return String(n);
  }

  // ---------------------------------------------------------------------------
  // Event wiring
  // ---------------------------------------------------------------------------

  /** Check if an event belongs to the currently displayed investigation. */
  private isRelevantEvent(investigationId: string): boolean {
    return this.isOpen && this.investigation?.id === investigationId;
  }

  private wireEvents(): void {
    // Listen for resource updates
    this.cleanupFns.push(
      eventBus.on('investigation:resource-update', ({ investigationId, resource }) => {
        if (this.isRelevantEvent(investigationId)) {
          this.setResourceState(resource);
        }
      }),
    );

    // Listen for coverage updates
    this.cleanupFns.push(
      eventBus.on('investigation:coverage-update', ({ investigationId, coverage }) => {
        if (this.isRelevantEvent(investigationId)) {
          this.setCoverageReport(coverage);
        }
      }),
    );

    // Listen for entity extraction (progressive disclosure)
    this.cleanupFns.push(
      eventBus.on('investigation:entity-extracted', ({ investigationId, entity }) => {
        if (this.isRelevantEvent(investigationId)) {
          this.addEntity(entity);
        }
      }),
    );

    // Listen for state transitions (collection log)
    // Mark the FROM state as completed — the TO state is the one we're entering
    this.cleanupFns.push(
      eventBus.on('investigation:state', ({ investigationId, from, to }) => {
        if (this.isRelevantEvent(investigationId)) {
          this.addCompletedPhase(from);
          // Update the investigation state for current-phase highlighting
          if (this.investigation) {
            this.investigation = { ...this.investigation, state: to };
          }
          this.renderCollectionLog();
          this.updateHeader();
        }
      }),
    );

    // Listen for synthesis completion — WP-S15.5
    this.cleanupFns.push(
      eventBus.on('investigation:synthesis-complete', ({ investigationId, assessment }) => {
        if (this.isRelevantEvent(investigationId)) {
          this.setDraftAssessment(assessment);
        }
      }),
    );
  }

  // ---------------------------------------------------------------------------
  // Entity merge UI — weak match review dialog
  // ---------------------------------------------------------------------------

  /** Add a weak match (from corroboration) to the graph for review. */
  addWeakMatch(match: CorroborationMatch): void {
    // Avoid duplicates
    const exists = this.weakMatches.some(
      m => (m.entityA.id === match.entityA.id && m.entityB.id === match.entityB.id)
        || (m.entityA.id === match.entityB.id && m.entityB.id === match.entityA.id),
    );
    if (!exists) {
      this.weakMatches.push(match);
    }
  }

  private showMergeDialog(match: CorroborationMatch): void {
    this.closeMergeDialog();

    const dialog = document.createElement('div');
    dialog.className = 'merge-dialog';
    dialog.style.cssText = `
      position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
      z-index: 20000; width: 600px; max-height: 80vh; overflow-y: auto;
      background: #0f1724; border: 1px solid #334155; border-radius: 6px;
      padding: 20px; color: #e2e8f0;
      font-family: 'JetBrains Mono', 'Cascadia Code', monospace;
      box-shadow: 0 20px 60px rgba(0,0,0,0.5);
    `;

    const title = document.createElement('div');
    title.style.cssText = 'font-size: 12px; color: #f59e0b; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 16px;';
    title.textContent = 'ENTITY MATCH REVIEW';
    dialog.appendChild(title);

    const reason = document.createElement('div');
    reason.style.cssText = 'font-size: 10px; color: #94a3b8; margin-bottom: 16px;';
    reason.textContent = `Match reason: ${escapeHtml(match.reason)} (${match.strength})`;
    dialog.appendChild(reason);

    // Side-by-side comparison
    const comparison = document.createElement('div');
    comparison.style.cssText = 'display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px;';

    comparison.appendChild(this.buildEntityCard(match.entityA, 'Entity A'));
    comparison.appendChild(this.buildEntityCard(match.entityB, 'Entity B'));
    dialog.appendChild(comparison);

    // Actions
    const actions = document.createElement('div');
    actions.style.cssText = 'display: flex; gap: 8px; justify-content: flex-end;';

    const mergeBtn = this.createDialogButton('MERGE', '#22c55e', () => {
      this.handleMergeAction(match, 'merge');
    });
    const dismissBtn = this.createDialogButton('DISMISS', '#ef4444', () => {
      this.handleMergeAction(match, 'dismiss');
    });
    const flagBtn = this.createDialogButton('FLAG FOR LATER', '#64748b', () => {
      this.handleMergeAction(match, 'flag');
    });

    actions.appendChild(flagBtn);
    actions.appendChild(dismissBtn);
    actions.appendChild(mergeBtn);
    dialog.appendChild(actions);

    // Backdrop
    const backdrop = document.createElement('div');
    backdrop.style.cssText = 'position: fixed; inset: 0; z-index: 19999; background: rgba(0,0,0,0.5);';
    backdrop.addEventListener('click', () => this.closeMergeDialog());

    this.mergeDialogEl = document.createElement('div');
    this.mergeDialogEl.appendChild(backdrop);
    this.mergeDialogEl.appendChild(dialog);
    document.body.appendChild(this.mergeDialogEl);
  }

  private buildEntityCard(entity: Entity, label: string): HTMLElement {
    const card = document.createElement('div');
    card.style.cssText = `
      background: #1a2332; border: 1px solid #1e293b; border-radius: 4px; padding: 12px;
    `;

    const headerEl = document.createElement('div');
    headerEl.style.cssText = 'font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px;';
    headerEl.textContent = label;
    card.appendChild(headerEl);

    const nameEl = document.createElement('div');
    nameEl.style.cssText = `font-size: 13px; font-weight: 600; color: ${TYPE_COLORS[entity.type] || '#e2e8f0'}; margin-bottom: 6px;`;
    nameEl.textContent = entity.name;
    card.appendChild(nameEl);

    const typeEl = document.createElement('div');
    typeEl.style.cssText = 'font-size: 10px; color: #94a3b8; margin-bottom: 4px;';
    typeEl.textContent = `Type: ${TYPE_LABELS[entity.type] || entity.type}`;
    card.appendChild(typeEl);

    if (entity.corroboration?.natoRating) {
      const natoEl = document.createElement('div');
      natoEl.style.cssText = 'font-size: 10px; color: #94a3b8; margin-bottom: 4px;';
      natoEl.textContent = `NATO: ${entity.corroboration.natoRating}`;
      card.appendChild(natoEl);
    }

    if (entity.provenance?.sourceUrl) {
      const srcEl = document.createElement('div');
      srcEl.style.cssText = 'font-size: 9px; color: #475569; word-break: break-all; margin-bottom: 4px;';
      srcEl.textContent = `Source: ${entity.provenance.sourceUrl}`;
      card.appendChild(srcEl);
    }

    if (entity.provenance?.extractionQuote) {
      const quoteEl = document.createElement('div');
      quoteEl.style.cssText = 'font-size: 9px; color: #64748b; font-style: italic; margin-top: 4px;';
      quoteEl.textContent = `"${entity.provenance.extractionQuote.slice(0, 120)}${entity.provenance.extractionQuote.length > 120 ? '...' : ''}"`;
      card.appendChild(quoteEl);
    }

    return card;
  }

  private createDialogButton(text: string, color: string, onClick: () => void): HTMLElement {
    const btn = document.createElement('button');
    btn.style.cssText = `
      padding: 6px 14px; border: 1px solid ${color}; border-radius: 3px;
      background: transparent; color: ${color}; cursor: pointer;
      font-size: 10px; font-weight: 600; letter-spacing: 0.5px;
      font-family: 'JetBrains Mono', monospace;
    `;
    btn.textContent = text;
    btn.addEventListener('click', onClick);
    btn.addEventListener('mouseenter', () => { btn.style.background = color + '22'; });
    btn.addEventListener('mouseleave', () => { btn.style.background = 'transparent'; });
    return btn;
  }

  private closeMergeDialog(): void {
    if (this.mergeDialogEl) {
      this.mergeDialogEl.remove();
      this.mergeDialogEl = null;
    }
  }

  private async handleMergeAction(match: CorroborationMatch, action: 'merge' | 'dismiss' | 'flag'): Promise<void> {
    if (!this.investigation) return;

    const investigationId = this.investigation.id;
    const state = this.investigation.state;

    if (action === 'merge') {
      await addLogEntry({
        investigationId,
        state,
        action: 'entity_merged',
        detail: {
          entityA: { id: match.entityA.id, name: match.entityA.name, type: match.entityA.type },
          entityB: { id: match.entityB.id, name: match.entityB.name, type: match.entityB.type },
          reason: match.reason,
          strength: match.strength,
        },
      });
      // Remove from weak matches list
      this.weakMatches = this.weakMatches.filter(m => m !== match);
    } else if (action === 'dismiss') {
      await addLogEntry({
        investigationId,
        state,
        action: 'entity_dismissed',
        detail: {
          entityA: { id: match.entityA.id, name: match.entityA.name },
          entityB: { id: match.entityB.id, name: match.entityB.name },
          reason: match.reason,
        },
      });
      // Remove from weak matches list
      this.weakMatches = this.weakMatches.filter(m => m !== match);
    } else {
      // Flag for later — keep in weak matches, log the action
      await addLogEntry({
        investigationId,
        state,
        action: 'analyst_intervention',
        detail: {
          action: 'flagged_for_later',
          entityA: { id: match.entityA.id, name: match.entityA.name },
          entityB: { id: match.entityB.id, name: match.entityB.name },
        },
      });
    }

    await updateLastActivity(investigationId);
    this.closeMergeDialog();
    this.refreshGraph();
  }
}

// ---------------------------------------------------------------------------
// Singleton + factory
// ---------------------------------------------------------------------------

let _instance: GraphPanel | null = null;

/** Get or create the singleton graph panel. */
export function getGraphPanel(): GraphPanel {
  if (!_instance) {
    _instance = new GraphPanel();
  }
  return _instance;
}

/** Open the graph view for an investigation. */
export function openGraphView(
  investigation: Investigation,
  entities: Entity[],
  relationships: Relationship[],
  onClose: () => void,
  weakMatches?: CorroborationMatch[],
): void {
  const panel = getGraphPanel();
  panel.open(investigation, entities, relationships, onClose, weakMatches);
}

/** Close the graph view. */
export function closeGraphView(): void {
  if (_instance) {
    _instance.close();
  }
}

/** Destroy the graph panel instance. */
export function destroyGraphPanel(): void {
  if (_instance) {
    _instance.destroy();
    _instance = null;
  }
}
